import React, {createRef, useState, useContext, useEffect, useRef} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import moment from "moment";
import {Button, Checkbox, Icon, Tooltip, useTheme} from "@material-ui/core";
import {
  Cancel,
  CheckCircle,
  Create,
  DescriptionOutlined,
  Event,
  AddCircleRounded,
  Block,
} from "@material-ui/icons";
import MaterialTable from "@material-table/core";
import {defaultComponentsScroll, MaterialTableBody, MaterialTableSummaryRow, FullWidthFilter} from "../../../controls/Material-Table-Components"
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import StringToJSX from "../../../functions/StringToJsx";
import {ColumnWidth } from "../../../blocks/ColumnHelper";
import useDimensions from "../../../blocks/useDimensions";
import IconButtonMenu from "../../../controls/IconButtonMenu";
import {PlaneTicket} from "../../../icons";
import {useAwbAllocatedStock, useUpdateAwbAllocatedStock}from "../../../services/awbStock";
import {AwbNumber} from "../../../controls";
import { Stack } from '@mui/material';
import ViewBooking from '../../../modules/Booking/components/ViewBooking';

const apiSitePrefix = getApiSitePrefix();


const AwbAllocatedStockList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const props = {
    ..._props
  }
  const {classes} = props;

  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const [state, setState] = useState({
    activeRow: null,
    selectedIndex: null,
    canEdit: props.canEdit||false,
    title: ""
  });

  let selectedRow = -1;

  const [searchCriteria, setSearchCriteria] = useState(__.has(props, "searchExtraKeys") && props.searchExtraKeys ? props.searchExtraKeys : ["EMPTY_SEARCH"]);

  const tableRef = createRef({state: {lastEditingRow: false}});
  const queryClient = useQueryClient();
  let extraKeys = searchCriteria;
  const allocatedStock = useAwbAllocatedStock({tableRef, queryClient, extraKeys});
  const handleUpdateAwbAllocatedStock = useUpdateAwbAllocatedStock();


  useEffect(() => {
    const setSearchValues = (params) => {
      const {awbAllocationUuid, companyName, dateAllocated, quantity, used} = params;
      const extraKeys = [];
      extraKeys.push(awbAllocationUuid);
      setSearchCriteria(extraKeys);
      const title = `'${companyName}' stock allocated on ${moment(dateAllocated).format("DD MMM, YYYY")} (${quantity - used} available)`;
      setState({...state, title: title});
    }

    if (eventBus) {
      eventBus.bind('AwbAllocatedStockList.search', setSearchValues);
    }
    return () => {
      eventBus.unbind('AwbAllocatedStockList.search', setSearchValues);
    }
  }, [eventBus, state]);


  async function handleViewBooking(record) {
    if (record && record.bookingUuid) {
      const {bookingUuid} = record;
      eventBus.trigger("ViewBooking.open",{open: true, bookingUuid});
    }
  }

  const handleToggleBlock = async(record) => {
    if (record) {
      record['blocked'] = !record.blocked;
      handleUpdateAwbAllocatedStock.mutate({
        extraKeys: extraKeys,
        record,
        postSuccess: () => {
        },
        postError: (data) => {
          if (props.onError) {
            props.onError(data);
          } else if (data.errorMessage) {
            SystemData.alert("Error", `${data.errorMessage}`);
          } else {
            SystemData.alert("Error", `An Unexpected Error has occurred.`);
          }
        }
      });
    }
  }
  
  const awbSearch = (fieldname) => {
    return  (term, rowData) => {
      const termUpper = `${term}`.replace("-","").toUpperCase();
      if (!__.has(rowData, fieldname)) return __.isEmpty(term);

      const awbPrefix =  rowData["awbPrefix"];
      const awbSerial = rowData["awbSerial"];

      if (`${awbPrefix}${awbSerial}`.replace("-","").startsWith(termUpper)) return true;
      if (`${awbSerial}`.startsWith(termUpper)) return true;
      if (`${awbSerial}`.endsWith(termUpper)) return true;

      return false;
    }
  }

  const flightSearch = (fieldname) => {
    return  (term, rowData) => {
      const termUpper = `${term}`.replace("-","").toUpperCase();
      if (!__.has(rowData, fieldname)) return __.isEmpty(term);

      const airlineCode =  rowData["airlineCode"];
      const flightNo = rowData["flightNo"];

      if (`${airlineCode}${flightNo}`.replace("-","").startsWith(termUpper)) return true;
      if (`${flightNo}`.startsWith(termUpper)) return true;

      return false;
    }
  }

  const renderActionIcon = (rowData) => {
    return (
      <PlaneTicket style={{width:24, height:24 }} color={(state.canEdit && !state.multipleSelectionActive ? (rowData.tableData.id === selectedRow ? 'secondary' : 'primary') : 'disabled')}/>
    );
  }

  const getColumns = (tableWidth) => {
    const baseColumn = {
      cellStyle: { fontSize:"0.7rem", padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
    };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(100, 4, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5", paddingLeft: 12}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Air Waybill",
        field: "awbPrefix",
        filterComponent: FullWidthFilter,
        render: rowData => {
          return (
            <AwbNumber
              fullWidth
              prefix={rowData.awbPrefix}
              serial={rowData.awbSerial}
              render
              onChange={(value) => {
              }}
            />
          )
        },
        customFilterAndSearch: awbSearch('awbPrefix')
      },
      {
        ...baseColumn,
        ...(ColumnWidth(370, undefined, undefined, {paddingLeft: 12, fontSize:"0.7rem"}, {paddingLeft: 12})),
        title: "Booking Reference",
        field: "bookingReference",
        editable: "never",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(290, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5", paddingLeft: 12}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Flight",
        field: "tripLegUuid",
        filterComponent: FullWidthFilter,
        render: (rowData)=> {
          const flight = rowData.tripLegUuid ?`${rowData.airlineCode}-${rowData.flightNo} on ${moment(rowData.flightDate).format("DD MMM, YYYY")}` : ``;
          return(<span>{flight}</span>)
        },
        customFilterAndSearch: flightSearch('airlineCode')
      },
      {
        ...baseColumn,
        ...ColumnWidth(74),
        title: "Action",
        field: "bookingUuid",
        render: (rowData)=> {
          return(
            <Stack direction="row" alignItems="center" spacing={0}>
              <Tooltip title="View Booking">
                <PlaneTicket
                  style={{width:24, height:24 }} 
                  color={(state.canEdit && rowData.bookingUuid ? (rowData.tableData.id === selectedRow ? 'secondary' : 'primary') : 'disabled')}
                  onClick={()=>{
                    if (rowData.bookingUuid) handleViewBooking(rowData);
                  }}
                />
              </Tooltip>
              <Tooltip title={rowData.blocked?"Unblock AWB":"Block AWB"}>
                <Block 
                  style={{width:24, height:24 }} 
                  color={(state.canEdit ? (rowData.tableData.id === selectedRow ? 'secondary' : 'primary') : 'disabled')}
                  disabled={!state.canEdit}                  
                  onClick={()=>handleToggleBlock(rowData)}
                />
              </Tooltip>
            </Stack>
          )
        },
        filterComponent: (props)=> {
          return(<></>
            // <IconButton
            //   hidden={spotBookings}
            //   disabled={!state.multipleSelectionActive}
            //   size={"small"}
            //   offset={-1}
            //   tooltip={"Confirm Selected Bookings"}
            //   onClick={(e)=>{handleMultipleConfirms()}}
            //   style={{margin: "4px 0px 0px 12px"}}
            // ><CheckCircle color={`${state.multipleSelectionActive?"primary":"disabled"}`}/></IconButton>
          )
        }
      }
    ];
  };

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:46;
  const bodyHeight = tableHeight - thead - 40;

  if (!allocatedStock.data || extraKeys.includes("EMPTY_SEARCH")) {
    return (
      <div style={{position: "absolute", width: "100%", top: "50%", textAlign: "center", alignItems: "center", justifyContent: "center"}}>
        <Button color={"primary"}>Select an Allocation record to view its waybills</Button>
      </div>
    )
  }
  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 0, position: "relative"}}>
      <div style={{background: "#ffffff", paddingTop: 16, paddingLeft: 20, paddingRight: 4, nowidth: "calc(100% - 24px)"}}>
        <div ref={tableContainerRef} className={state.canEdit?classes.bookingQueueListEdit:classes.bookingQueueList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 16}px)`}}>
        <MaterialTable
          icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
          tableRef={tableRef}
          style={{
            border: "none", //"1px solid #aaaaaa",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            rowStyle: (rowData)=> (rowData.blocked?{textDecoration: "line-through", backgroundColor: "rgba(0,0,0,0.1"}:{}),
            highlightRowStyle: {backgroundColor: "#F69259"},
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "4px 2px",
            },
            filterCellStyle: {
              padding: "4px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: 2,
              height: 46
            },
            filterRowStyle: {
              paddingLeft: 0,
              width: "100%",
            },
            toolbar: true,
            toolbarButtonAlignment: "right",
            detailPanelType: "single",
            padding: 'dense',
            paging: false,
            filtering: true,
            search: false,
            searchFieldAlignment: "left",
            columnResizable: false,
            selection: false,
          }}
          components={{
            ...defaultComponentsScroll,
            Body: (props) => (
              <MaterialTableBody
                {...props}
                rowHeight={46}
                overscanRowCount={10}
                addVirtualScrolling={true}
                bodyHeight={bodyHeight}
                headerHeight={thead}
                filterRowHeight={55}
              />
            ),
          }}
          localization={{
            header: {
              actions: 'Action'
            },
          }}
          title={state.title}
          columns={getColumns(tableWidth)}
          data={allocatedStock.data}
        />
        </div>
      </div>
      <ViewBooking />

    </div>
  );

}

export default AwbAllocatedStockList;
